"use client";

import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { orpc } from "@/utils/orpc";

// Infer the Product type from the oRPC client
type Product = Awaited<ReturnType<typeof orpc.shopify.getProduct.call>>;

// Type for a validated product (non-null and has required fields)
export type ValidProduct = NonNullable<Product> & {
  id: string;
  title: string;
  handle: string;
};

/**
 * Enhanced React Query hook for fetching a single product by handle
 * Returns additional computed properties and better type safety
 */
export function useProduct(
  handle: string | null | undefined,
  options?: Partial<
    UseQueryOptions<
      Product | null,
      Error,
      Product | null,
      readonly [string, string]
    >
  >
) {
  const query = useQuery({
    queryKey: ["product", handle || ""] as const,
    queryFn: async () => {
      if (!handle) return null;

      const result = await orpc.shopify.getProduct.call({
        handle,
      });
      return result;
    },
    enabled: Boolean(handle),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404-like errors (product not found)
      if (
        error instanceof Error &&
        (error.message.includes("not found") || error.message.includes("404"))
      ) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });

  return {
    ...query,
    // Additional computed properties
    isValidProduct: isValidProduct(query.data),
    productData: query.data ? getProductData(query.data) : null,
  };
}

/**
 * Enhanced type guard that ensures all required fields are present
 */
export function isValidProduct(
  product: Product | null | undefined
): product is ValidProduct {
  return Boolean(
    product?.id &&
      product?.title &&
      product?.handle &&
      product?.images &&
      product?.variants &&
      product?.options &&
      product?.priceRange
  );
}

/**
 * Comprehensive product data extractor
 * Returns all derived values in one place with safe defaults
 */
export function getProductData(product: Product | null | undefined) {
  if (!product) {
    return {
      primaryImage: null,
      hasMultipleImages: false,
      defaultVariant: null,
      priceRange: "£0.00",
      isAvailable: false,
      hasOptions: false,
      hasTags: false,
      quantityText: "Out of Stock",
    };
  }

  const primaryImage = product.images?.[0] || null;
  const hasMultipleImages = (product.images?.length || 0) > 1;
  const defaultVariant = getDefaultVariant(product);
  const priceRange = getProductPriceRange(product);
  const isAvailable = isProductAvailable(product);
  const hasOptions = (product.options?.length || 0) > 0;
  const hasTags = (product.tags?.length || 0) > 0;

  // Smart quantity text
  const quantityText = (() => {
    if (
      defaultVariant?.quantityAvailable !== null &&
      defaultVariant?.quantityAvailable !== undefined
    ) {
      return `${defaultVariant.quantityAvailable} available`;
    }
    return isAvailable ? "In Stock" : "Out of Stock";
  })();

  return {
    primaryImage,
    hasMultipleImages,
    defaultVariant,
    priceRange,
    isAvailable,
    hasOptions,
    hasTags,
    quantityText,
  };
}

/**
 * Get the primary product image URL with safe fallback
 */
export function getProductImageUrl(
  product: Product | null | undefined
): string | null {
  return product?.images?.[0]?.url || null;
}

/**
 * Get the product price range as a formatted string with safe fallback
 */
export function getProductPriceRange(
  product: Product | null | undefined
): string {
  if (!product?.priceRange) {
    return "£0.00";
  }

  const minPrice = Number.parseFloat(product.priceRange.minVariantPrice.amount);
  const maxPrice = Number.parseFloat(product.priceRange.maxVariantPrice.amount);

  if (minPrice === maxPrice) {
    return `£${minPrice.toFixed(2)}`;
  }

  return `£${minPrice.toFixed(2)} - £${maxPrice.toFixed(2)}`;
}

/**
 * Check if product is available for sale with safe fallback
 */
export function isProductAvailable(
  product: Product | null | undefined
): boolean {
  if (!product) return false;

  return (
    product.availableForSale &&
    product.variants.some((variant) => variant.availableForSale)
  );
}

/**
 * Get the default variant for a product with safe fallback
 */
export function getDefaultVariant(product: Product | null | undefined) {
  if (!product?.variants || product.variants.length === 0) {
    return null;
  }

  // Return the first available variant, or the first variant if none are available
  return (
    product.variants.find((variant) => variant.availableForSale) ||
    product.variants[0]
  );
}

/**
 * Custom hook for managing product selection state
 */
export function useProductSelection(product: ValidProduct) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(
    () => getDefaultVariant(product)?.id || null
  );

  // Reset selections when product changes
  useEffect(() => {
    setSelectedImage(0);
    setQuantity(1);
    setSelectedVariant(getDefaultVariant(product)?.id || null);
  }, [product]);

  return {
    selectedImage,
    setSelectedImage,
    quantity,
    setQuantity,
    selectedVariant,
    setSelectedVariant,
  };
}
